import React, { useContext, useState } from "react";
import {
  Visibility,
  Settings,
  ReportProblem as IncidentIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import { useQuery } from "react-query";
import TableComponent from "../../TableComponent/TableComponent";
import IncidentDetailsModal from "../Modal/IncidentDetailsModal";
import ChangeIncidentStatus from "../Modal/ChangeIncidentStatus";
import { QK } from "../../../base/qk";
import { getIncidetsList } from "../../../api/services/notification";
import { formatDateField, formatTimeToAmPm } from "../../../base/FormatTime";
import MyContext from "../../../context/MyContext";

const IncidentContent = () => {
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showIncidentStatusModal, setShowIncidentStatusModal] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState(null);
  const incidentsPerPage = 10;
  const { locationID } = useContext(MyContext);

  const { data: incidents, isLoading } = useQuery(
    [QK.incidentList, currentPage, locationID],
    () => getIncidetsList(currentPage, locationID),
    { keepPreviousData: true, enabled: !!currentPage || !!locationID }
  );

  const totalRecords = incidents?.count || 0;
  const totalPages = Math.ceil(totalRecords / incidentsPerPage);

  const handlePageChange = (event, page) => setCurrentPage(page);

  const handleView = (incident) => {
    setSelectedIncidentId(incident.id);
    setShowIncidentModal(true);
  };

  const handleSettings = (incident) => {
    setSelectedIncidentId(incident.id);
    setShowIncidentStatusModal(true);
  };

  const statusColors = {
    reported: { bg: "#EFF8FF", color: "#175CD3", border: "#B2DDFF" },
    under_investigation: { bg: "#FFFAEB", color: "#B54708", border: "#FEDF89" },
    review_required: { bg: "#FEF3F2", color: "#B42318", border: "#FECDCA" },
    closed: { bg: "#ECFDF3", color: "#027A48", border: "#ABEFC6" },
  };

  const columns = [
    {
      field: "reference_number",
      headerName: "Ref. No.",
      render: (value) => (
        <Typography fontSize="0.78rem" fontWeight={600} color="#1A1A2E">
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Reported On",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {formatDateField(value) || "—"}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Time",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {formatTimeToAmPm(value) || "—"}
        </Typography>
      ),
    },
    {
      field: "incident_type_display",
      headerName: "Type",
      render: (value, row) => (
        <Typography fontSize="0.78rem" fontWeight={600} color="#1A1A2E">
          {value || row?.incident_type || "—"}
        </Typography>
      ),
    },
    {
      field: "incident_sub_type",
      headerName: "Sub Type",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "area_detail",
      headerName: "Area",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {value?.name || "—"}
        </Typography>
      ),
    },
    {
      field: "who_involved",
      headerName: "Who Involved",
      render: (value) => {
        if (!value?.length) return "—";
        const first = value[0];
        return (
          <Typography fontSize="0.78rem" color="#475467">
            {first.involver_name || first.other_name || "—"}
          </Typography>
        );
      },
    },
    {
      field: "reported_by_detail",
      headerName: "Reported By",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {value?.name || "—"}
        </Typography>
      ),
    },
    {
      field: "status_display",
      headerName: "Status",
      render: (value, row) => {
        const c = statusColors[row?.status] || {
          bg: "#F2F4F7",
          color: "#475467",
          border: "#EAECF0",
        };
        return (
          <Chip
            label={value || row?.status || "—"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              height: 22,
              bgcolor: c.bg,
              color: c.color,
              border: `1px solid ${c.border}`,
            }}
          />
        );
      },
    },
    {
      field: "hospitalised",
      headerName: "Hospitalised",
      render: (value) => (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.68rem",
            height: 20,
            bgcolor: value ? "#FEF3F2" : "#F2F4F7",
            color: value ? "#B42318" : "#475467",
            border: `1px solid ${value ? "#FECDCA" : "#EAECF0"}`,
          }}
        />
      ),
    },
    {
      field: "called_999",
      headerName: "999 Called",
      render: (value) => (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.68rem",
            height: 20,
            bgcolor: value ? "#FEF3F2" : "#F2F4F7",
            color: value ? "#B42318" : "#475467",
            border: `1px solid ${value ? "#FECDCA" : "#EAECF0"}`,
          }}
        />
      ),
    },
    {
      field: "gp_called",
      headerName: "GP Called",
      render: (value) => (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.68rem",
            height: 20,
            bgcolor: value ? "#FFFAEB" : "#F2F4F7",
            color: value ? "#B54708" : "#475467",
            border: `1px solid ${value ? "#FEDF89" : "#EAECF0"}`,
          }}
        />
      ),
    },
    {
      field: "updated_at",
      headerName: "Last Reviewed",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {formatDateField(value) || "—"}
        </Typography>
      ),
    },
    {
      field: "incident_description",
      headerName: "Summary",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {value
            ? value.length > 60
              ? `${value.slice(0, 60)}…`
              : value
            : "—"}
        </Typography>
      ),
    },
    {
      field: "pending_actions",
      headerName: "Pending Actions",
      render: (value) => (
        <Typography fontSize="0.78rem" color="#475467">
          {value || "0"}
        </Typography>
      ),
    },
  ];

  const actions = [
    {
      icon: <Visibility />,
      color: "primary",
      handler: handleView,
      tooltip: "View incident details",
    },
    {
      icon: <Settings />,
      color: "default",
      handler: handleSettings,
      tooltip: "Change incident status",
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #EAECF0",
          p: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IncidentIcon
                sx={{ fontSize: 22, color: theme.palette.primary.main }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#1A1A2E"
                fontSize="1.05rem"
              >
                Incidents
              </Typography>
              <Typography variant="caption" color="#6B7280" fontSize="0.72rem">
                Manage and review all reported incidents
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: "none",
              px: 2,
              "&:hover": { boxShadow: "none" },
            }}
          >
            New Incident
          </Button>
        </Box>
      </Card>

      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #EAECF0",
          overflow: "hidden",
        }}
      >
        <TableComponent
          columns={columns}
          data={incidents?.results || []}
          actions={actions}
          isLoading={isLoading}
          pagination={{ totalPages, currentPage }}
          onPageChange={handlePageChange}
          size="small"
        />
      </Card>

      <IncidentDetailsModal
        open={showIncidentModal}
        handleClose={() => setShowIncidentModal(false)}
        id={selectedIncidentId}
      />
      <ChangeIncidentStatus
        id={selectedIncidentId}
        handleClose={() => setShowIncidentStatusModal(false)}
        open={showIncidentStatusModal}
      />
    </Box>
  );
};

export default IncidentContent;